import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Loader2, ShieldCheck, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';
import { formatApiError } from '../lib/apiError';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, password });
      toast.success('Password updated. Please sign in with your new password.');
      navigate('/login');
    } catch (err) {
      toast.error(formatApiError(err, 'Could not reset password'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F1F5F9] flex flex-col">
        <header className="bg-[#0f1b3d] text-white px-6 py-5">
          <Logo size="sm" variant="light" linkTo="/" />
        </header>
        <main className="flex-1 flex items-center justify-center p-6">
          <Card className="w-full max-w-md shadow-lg border-0" data-testid="reset-invalid-token-card">
            <CardHeader className="text-center pb-6">
              <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-3">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
              <CardTitle className="text-2xl font-bold text-slate-900">Invalid link</CardTitle>
              <CardDescription>This reset link is missing a token. Please request a new one.</CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/forgot-password">
                <Button className="w-full h-12 rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white">
                  Request new link
                </Button>
              </Link>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F1F5F9] flex flex-col">
      <header className="bg-[#0f1b3d] text-white px-6 py-5">
        <Logo size="sm" variant="light" linkTo="/" />
      </header>
      <main className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md shadow-lg border-0">
          <CardHeader className="space-y-1 pb-6">
            <CardTitle className="text-2xl font-bold text-slate-900 font-['Public_Sans']">
              Set a new password
            </CardTitle>
            <CardDescription className="text-slate-500">
              Choose a new password for your ShiftCover365 account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700 font-medium">New password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="At least 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="h-14 rounded-xl bg-slate-50 border-slate-200"
                  data-testid="reset-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm" className="text-slate-700 font-medium">Confirm password</Label>
                <Input
                  id="confirm"
                  type="password"
                  placeholder="Re-enter password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={6}
                  className="h-14 rounded-xl bg-slate-50 border-slate-200"
                  data-testid="reset-password-confirm-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-14 rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white font-semibold text-lg"
                disabled={loading}
                data-testid="reset-password-submit-btn"
              >
                {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Updating...</> : 'Update password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
